import { trackException } from '@/core/appInsights/appInsights'
import { authService } from '@/core/sso/oidc/AuthService'
import { HttpError } from '@/misc/HttpError'
import { injectable } from 'inversify-props'
import type IApiClient from './IApiClient'
import type { IApiRequestOptions } from './IApiRequestOptions'

@injectable()
export class ApiClient implements IApiClient {
  private readonly ContentDisposition: string = 'Content-Disposition'
  private readonly baseUrl: string = `${import.meta.env.VITE_API_URL}`
  private readonly apiKey: string = `${import.meta.env.VITE_API_KEY}`
  private authKey: string = `${import.meta.env.VITE_AUTH_KEY}`

  constructor(
    private readonly onUnauthorized: () => void,
    baseUrl: string | null = null,
    apiKey: string | null = null
  ) {
    this.baseUrl = baseUrl ?? this.baseUrl
    this.apiKey = apiKey ?? this.apiKey
  }

  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'API-Key': this.apiKey
    }

    if (!this.authKey || this.authKey === 'undefined') {
      this.authKey = 'Authorization'
    }
    headers[this.authKey] = `Bearer ${authService.user.value?.access_token}`

    return headers
  }

  private async handleResponse<T>(response: Response, options?: IApiRequestOptions): Promise<T> {
    this.validateResponse(response, options)
    const jsonResp = await this.isJsonResponse(response)

    if (jsonResp) {
      return (await response.json()) as Promise<T>
    }

    return undefined as T
  }

  public async isJsonResponse(response: Response) {
    const contentType = response.headers.get('content-type')
    if (!contentType?.includes('application/json')) {
      return false
    }

    try {
      await response.clone().json()
      return true
    } catch {
      return false
    }
  }

  public async get<T>(endpoint: string, abortController?: AbortController, options?: IApiRequestOptions): Promise<T> {
    const response = await fetch(`${this.baseUrl}/${endpoint}`, {
      method: 'GET',
      headers: await this.getHeaders(),
      signal: abortController?.signal
    })
    return this.handleResponse<T>(response, options)
  }

  public async post<T>(endpoint: string, data: object, abortController?: AbortController): Promise<T> {
    const response = await fetch(`${this.baseUrl}/${endpoint}`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(data),
      signal: abortController?.signal
    })
    return this.handleResponse<T>(response)
  }

  public async postNoBody<T>(endpoint: string, abortController?: AbortController): Promise<T> {
    const response = await fetch(`${this.baseUrl}/${endpoint}`, {
      method: 'POST',
      headers: await this.getHeaders(),
      signal: abortController?.signal
    })
    return this.handleResponse<T>(response)
  }

  public async put<T>(endpoint: string, data: object, abortController?: AbortController): Promise<T> {
    const response = await fetch(`${this.baseUrl}/${endpoint}`, {
      method: 'PUT',
      headers: await this.getHeaders(),
      body: JSON.stringify(data),
      signal: abortController?.signal
    })
    return this.handleResponse<T>(response)
  }

  public async getBlob(endpoint: string, abortController?: AbortController): Promise<{ blob: Blob; fileName?: string }> {
    const response = await fetch(`${this.baseUrl}/${endpoint}`, {
      method: 'GET',
      headers: await this.getHeaders(),
      signal: abortController?.signal,
      mode: 'cors'
    })

    this.validateResponse(response)

    const contentDisposition = response.headers.get(this.ContentDisposition)
    let fileName: string | undefined

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match && match[1]) {
        fileName = match[1]
      }
    }
    const blob = await response.blob()
    return { blob, fileName }
  }

  public async postBlob(endpoint: string, data: object, abortController?: AbortController): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/${endpoint}`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(data),
      signal: abortController?.signal
    })
    this.validateResponse(response)
    return response.blob()
  }

  public async postBlobWithFileName(endpoint: string, data: object, abortController?: AbortController): Promise<{ blob: Blob; fileName?: string }> {
    const response = await fetch(`${this.baseUrl}/${endpoint}`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(data),
      signal: abortController?.signal
    })

    this.validateResponse(response)

    const contentDisposition = response.headers.get(this.ContentDisposition)
    let fileName: string | undefined

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match && match[1]) {
        fileName = match[1]
      }
    }
    const blob = await response.blob()
    return { blob, fileName }
  }

  public async postFormData<T>(endpoint: string, data: FormData, abortController?: AbortController): Promise<T> {
    const baseHeaders = await this.getHeaders()
    const headers = new Headers(baseHeaders)
    headers.delete('Content-Type')
    const response = await fetch(`${this.baseUrl}/${endpoint}`, {
      method: 'POST',
      headers,
      body: data,
      signal: abortController?.signal
    })

    return this.handleResponse<T>(response)
  }

  public async delete<T>(endpoint: string, abortController?: AbortController): Promise<T> {
    const response = await fetch(`${this.baseUrl}/${endpoint}`, {
      method: 'DELETE',
      headers: await this.getHeaders(),
      signal: abortController?.signal
    })
    return this.handleResponse<T>(response)
  }

  public async putBlob(uploadUrl: string, file: File, abortController?: AbortController): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': file.type
      },
      body: file,
      signal: abortController?.signal
    })

    if (!response.ok) {
      const ex = new HttpError(response.statusText, `Blob upload failed! Status: ${response.status}`, response.status)
      trackException({ error: ex })
      throw ex
    }
  }

  private validateResponse(response: Response, options?: IApiRequestOptions) {
    if (!response.ok) {
      const ex = new HttpError(response.statusText, `HTTP error! Status: ${response.status}`, response.status)
      trackException({ error: ex })
      if (options) {
        switch (response.status) {
          case 401:
            options.onUnauthorized?.()
            this.onUnauthorized()
            break
          case 404:
            options.onNotFound?.()
            break
          case 500:
            options.onServerError?.()
            break
          default:
            throw ex
        }
      } else {
        throw ex
      }
    }
  }
}
